// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { Loader2, Save, Store } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useChangeUserStoreMutation } from "@/api/hooks/use-users";
import { VirtualSelectPopover } from "@/components/shared/virtual-select-popover";
import { WizardPane } from "@/components/shared/wizard-pane";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useBackofficeContext } from "@/hooks/use-backoffice-context";

interface ChangeStoreDialogProps {
  user: { publicId: string; email: string; fullName?: string; defaultStoreName: string } | null;
  onClose: () => void;
}

export function ChangeStoreDialog({ user, onClose }: ChangeStoreDialogProps) {
  const { t } = useTranslation("back-office");
  const { data: context, isLoading: contextLoading } = useBackofficeContext();
  const lastUser = useRef(user);
  if (user) lastUser.current = user;
  const display = user ?? lastUser.current;
  const changeStore = useChangeUserStoreMutation(display?.publicId ?? "");

  const [selectedStoreName, setSelectedStoreName] = useState("");
  const [removePrevious, setRemovePrevious] = useState(false);

  useEffect(
    function syncSelectedStoreFromUser() {
      if (user) {
        setSelectedStoreName(user.defaultStoreName);
        setRemovePrevious(false);
      }
    },
    [user],
  );

  const hasStoreChange = !!selectedStoreName && selectedStoreName !== display?.defaultStoreName;

  async function onSubmit() {
    try {
      await changeStore.mutateAsync({
        defaultStoreId: selectedStoreName,
        removePreviousDefault: removePrevious,
      });
      toast.success(t("users.notifications.updated", { name: display?.fullName ?? display?.email }));
      onClose();
    } catch {
      // Error toast handled by API middleware
    }
  }

  return (
    <Dialog open={!!user} onOpenChange={() => onClose()}>
      <DialogContent id="dialog-change-store" className="sm:max-w-[33rem] p-0 gap-0 overflow-hidden flex flex-col">
        <div className="flex flex-row">
          <WizardPane icon={Store} />
          <div className="flex-1 p-6 flex flex-col gap-4 min-w-0">
            <DialogHeader>
              <DialogTitle>{t("users.changeStoreDialog.title")}</DialogTitle>
              <DialogDescription>
                {t("users.changeStoreDialog.description")}{" "}
                {display?.fullName ? (
                  <>
                    <span className="font-semibold text-foreground">{display.fullName}</span> ({display.email})
                  </>
                ) : (
                  <span className="font-semibold text-foreground">{display?.email}</span>
                )}
                .
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              <Label>{t("users.changeStoreDialog.storeLabel")}</Label>
              <VirtualSelectPopover
                value={selectedStoreName}
                onChange={setSelectedStoreName}
                options={(context?.stores ?? []).map((s) => ({ label: s.display, value: s.value }))}
                placeholder={t("users.changeStoreDialog.selectStore")}
                searchPlaceholder={t("users.changeStoreDialog.searchStores")}
                emptyText={t("users.changeStoreDialog.noStores")}
                loading={contextLoading}
              />
            </div>
            <div className="flex items-center gap-3">
              <Switch id="remove-previous-default" checked={removePrevious} onCheckedChange={setRemovePrevious} />
              <Label htmlFor="remove-previous-default">{t("users.changeStoreDialog.removePrevious")}</Label>
            </div>
          </div>
        </div>
        <DialogFooter className="mx-0 mb-0 rounded-none rounded-b-lg">
          <Button variant="outline" onClick={onClose}>
            {t("buttons.cancel")}
          </Button>
          <Button onClick={onSubmit} disabled={!hasStoreChange || changeStore.isPending}>
            {changeStore.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {t("users.changeStoreDialog.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
